import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { 
  FileText, 
  Search, 
  ArrowUpDown, 
  ArrowUp, 
  ArrowDown, 
  Eye,
  History,
  Info
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { payslipsApi } from '../../../api/payslipsApi';
import { formatDate } from '../../../utils/stringUtils';

const DistributionLogs = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('generatedDate');
  const [sortOrder, setSortOrder] = useState('desc');

  // Fetch all payslip batches (which are the distribution logs)
  const { data: batches = [], isLoading } = useQuery({
    queryKey: ['payslipBatches'],
    queryFn: async () => {
      const response = await payslipsApi.getPayslipBatches();
      if (response && Array.isArray(response.data)) {
        return response.data;
      }
      return response || [];
    }
  });

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const filteredBatches = batches.filter(batch => 
    (batch.payGroupName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (batch.payrollMonth || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const sortedBatches = [...filteredBatches].sort((a, b) => {
    let aVal = a[sortField];
    let bVal = b[sortField];

    if (sortField === 'generatedDate') {
      aVal = new Date(aVal || 0).getTime();
      bVal = new Date(bVal || 0).getTime();
    }

    if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
    return 0;
  });

  const renderSortIcon = (field) => {
    if (sortField !== field) return <ArrowUpDown className="w-3 h-3 text-gray-400" />;
    return sortOrder === 'asc' ? <ArrowUp className="w-3 h-3 text-[#0F2942]" /> : <ArrowDown className="w-3 h-3 text-[#0F2942]" />;
  };

  return (
    <div className="space-y-6">
      {/* Header section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <History className="h-5 w-5 text-[#0F2942]" />
              Distribution Logs
            </h1>
            <p className="text-sm text-gray-500 mt-1">Audit trail of all generated payslip batches and salary distributions.</p>
          </div>
          
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by pay group or month..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#0F2942]/50/20 outline-none w-full md:w-80"
            />
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm">
          <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Total Distributions</p>
          <p className="text-2xl font-bold text-gray-900">{batches.length}</p>
        </div>
        <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm">
          <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Total Net Disbursed</p>
          <p className="text-2xl font-bold text-green-600">₹{batches.reduce((sum, b) => sum + (b.totalNetPay || 0), 0).toLocaleString('en-IN')}</p>
        </div>
        <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm">
          <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Last Run Date</p>
          <p className="text-2xl font-bold text-[#0F2942]">
            {batches[0]?.generatedDate ? formatDate(batches[0].generatedDate, { includeTime: false }) : 'N/A'}
          </p>
        </div>
      </div>

      {/* Logs Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th 
                  className="px-6 py-4 text-xs font-bold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('generatedDate')}
                >
                  <div className="flex items-center gap-2">
                    Generation Time {renderSortIcon('generatedDate')}
                  </div>
                </th>
                <th className="px-6 py-4 text-xs font-bold text-gray-600 uppercase tracking-wider">Pay Group</th>
                <th className="px-6 py-4 text-xs font-bold text-gray-600 uppercase tracking-wider text-right">Headcount</th>
                <th className="px-6 py-4 text-xs font-bold text-gray-600 uppercase tracking-wider text-right">Total Net Pay</th>
                <th className="px-6 py-4 text-xs font-bold text-gray-600 uppercase tracking-wider text-center">Generated By</th>
                <th className="px-6 py-4 text-xs font-bold text-gray-600 uppercase tracking-wider text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {isLoading ? (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center text-gray-500">
                    <div className="flex items-center justify-center gap-2">
                      <div className="h-5 w-5 border-2 border-[#0F2942] border-t-transparent rounded-full animate-spin"></div>
                      Loading logs...
                    </div>
                  </td>
                </tr>
              ) : sortedBatches.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center text-gray-400 italic">
                    No distribution logs found.
                  </td>
                </tr>
              ) : (
                sortedBatches.map((batch) => (
                  <tr key={batch.id} className="hover:bg-gray-50/50 transition-colors group">
                    <td className="px-6 py-4">
                      <div className="text-sm font-bold text-gray-900">
                        {formatDate(batch.generatedDate || batch.createdAt, { includeTime: false })}
                      </div>
                      <div className="text-xs text-[#0F2942] font-medium">
                        {new Date(batch.generatedDate || batch.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="h-8 w-8 rounded-full bg-blue-50 flex items-center justify-center">
                          <FileText className="h-4 w-4 text-[#0F2942]" />
                        </div>
                        <div>
                          <div className="text-sm font-bold text-gray-900">{batch.payGroupName}</div>
                          <div className="text-[10px] text-gray-400 uppercase font-medium">{batch.payrollMonth}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right text-sm font-medium text-gray-700">
                      {batch.totalEmployees || 0}
                    </td>
                    <td className="px-6 py-4 text-right text-sm font-bold text-gray-900">
                      ₹{(batch.totalNetPay || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                    </td>
                    <td className="px-6 py-4 text-center">
                      {(() => {
                        const creator = batch.generatedBy || batch.createdBy;
                        const displayName = typeof creator === 'object' ? creator.name : (creator || 'Admin');
                        const initial = typeof displayName === 'string' ? displayName.charAt(0) : 'A';
                        
                        return (
                          <div className="flex items-center justify-center gap-2">
                            <div className="h-6 w-6 rounded-full bg-gray-100 flex items-center justify-center text-[10px] font-bold text-gray-600 border border-gray-200 uppercase">
                              {initial}
                            </div>
                            <span className="text-xs font-semibold text-gray-700">{displayName}</span>
                          </div>
                        );
                      })()}
                    </td>
                    <td className="px-6 py-4 text-center">
                      <button 
                        onClick={() => navigate(`/payslips/batch/${batch.id}`)}
                        className="p-2 text-gray-400 hover:text-[#0F2942] hover:bg-blue-50 rounded-lg transition-all"
                        title="View Details"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      
      {/* Help Note */}
      <div className="bg-blue-50 border border-blue-100 p-4 rounded-xl flex gap-3">
        <Info className="h-5 w-5 text-[#0F2942] shrink-0" />
        <p className="text-sm text-blue-800 leading-relaxed">
          <strong>Note:</strong> These logs capture every instance of payslip generation. If manual adjustments or holds were applied during a run, they are recorded within the specific batch details. Click the <Eye className="inline h-3 w-3" /> icon to audit individual employee adjustments.
        </p>
      </div>
    </div>
  );
};

export default DistributionLogs;
